import { useEffect, useMemo, useRef, useState } from 'react'
import { useColorMode } from '@/color-mode'
import './styles.css'

const OLD = 'v4.28'
const NEW = 'v4.36'

type NodeStatus = 'serving' | 'drain' | 'upg' | 'join' | 'absent'

interface NodeState {
  s: NodeStatus
  v: 'old' | 'new' | null
  c: number
  l?: string
}

interface Metrics {
  nodes: string
  clients: string
  state: 'Healthy' | 'Mixed version' | 'Rebalancing'
  tput: string
}

type EventTag = 'cluster' | 'clients' | 'safety' | 'observ'

interface Step {
  ph: string
  phc: 'hmq-ph-base' | 'hmq-ph-up' | 'hmq-ph-scale'
  title: string
  n: [NodeState, NodeState, NodeState, NodeState]
  m: Metrics
  ev: [EventTag, string][]
}

const STEPS: Step[] = [
  {
    ph: 'Baseline',
    phc: 'hmq-ph-base',
    title: `Steady state, three-node cluster on ${OLD}`,
    n: [
      { s: 'serving', v: 'old', c: 10000 },
      { s: 'serving', v: 'old', c: 10000 },
      { s: 'serving', v: 'old', c: 10000 },
      { s: 'absent', v: null, c: 0, l: 'Not provisioned' },
    ],
    m: { nodes: '3', clients: '≈30,000', state: 'Healthy', tput: '≈452k/s' },
    ev: [
      [
        'cluster',
        'Three-node cluster healthy, replication factor 2, every partition in sync across node-1/2/3.',
      ],
      [
        'clients',
        '≈30,000 MQTT clients connected, spread round-robin by the load balancer (~10,000 per node).',
      ],
      [
        'safety',
        `All nodes run HiveMQ ${OLD}. Target ${NEW} confirmed as a supported in-place upgrade path.`,
      ],
      [
        'observ',
        'Throughput ≈452k msg/s · 0 dropped messages, this is the baseline to compare against.',
      ],
    ],
  },
  {
    ph: 'Scale out',
    phc: 'hmq-ph-scale',
    title: `Provision node-4 on the new version ${NEW}`,
    n: [
      { s: 'serving', v: 'old', c: 10000 },
      { s: 'serving', v: 'old', c: 10000 },
      { s: 'serving', v: 'old', c: 10000 },
      { s: 'join', v: 'new', c: 0 },
    ],
    m: { nodes: '4', clients: '≈30,000', state: 'Mixed version', tput: '≈451k/s' },
    ev: [
      [
        'cluster',
        `node-4 boots on ${NEW}, runs cluster discovery and sends a JOIN request to the existing peers.`,
      ],
      [
        'cluster',
        `A mixed-version cluster forms (${OLD} + ${NEW}), explicitly supported for the duration of a rolling upgrade.`,
      ],
      [
        'safety',
        'node-4 is deliberately kept out of the load balancer pool until its readiness probe passes.',
      ],
      [
        'observ',
        'The existing 30,000 clients are untouched, node-4 is still warming up with 0 connections.',
      ],
    ],
  },
  {
    ph: 'Scale out',
    phc: 'hmq-ph-scale',
    title: 'node-4 syncs and joins the load balancer pool',
    n: [
      { s: 'serving', v: 'old', c: 7500 },
      { s: 'serving', v: 'old', c: 7500 },
      { s: 'serving', v: 'old', c: 7500 },
      { s: 'serving', v: 'new', c: 7500 },
    ],
    m: { nodes: '4', clients: '≈30,000', state: 'Rebalancing', tput: '≈450k/s' },
    ev: [
      [
        'cluster',
        'Partition rebalance, replicas of client sessions, queued messages and retained messages copy onto node-4.',
      ],
      ['safety', 'node-4 health endpoint turns green and the readiness probe passes.'],
      [
        'clients',
        'The load balancer adds node-4 to the round-robin pool; connections now spread four ways.',
      ],
      [
        'observ',
        'Four nodes each serve ≈7,500 clients, spare capacity is now in place for the upgrade.',
      ],
    ],
  },
  {
    ph: 'Rolling upgrade',
    phc: 'hmq-ph-up',
    title: `Upgrade node-1: drain, leave cluster, install ${NEW}`,
    n: [
      { s: 'upg', v: 'new', c: 0 },
      { s: 'serving', v: 'old', c: 10000 },
      { s: 'serving', v: 'old', c: 10000 },
      { s: 'serving', v: 'new', c: 10000 },
    ],
    m: { nodes: '3', clients: '≈30,000', state: 'Rebalancing', tput: '≈449k/s' },
    ev: [
      [
        'clients',
        'node-1 is removed from the load balancer rotation; its clients gracefully disconnect.',
      ],
      [
        'clients',
        'They reconnect through the LB and round-robin onto node-2/3/4, persistent sessions resume via session takeover.',
      ],
      [
        'cluster',
        'node-1 leaves the cluster gracefully; the replica partitions it held migrate to node-2/3/4.',
      ],
      [
        'safety',
        `node-1 is stopped and HiveMQ ${NEW} is installed, three nodes keep serving, so capacity headroom holds.`,
      ],
      [
        'observ',
        '0 messages lost, in-flight QoS 1/2 messages were preserved by replicas on the surviving nodes.',
      ],
    ],
  },
  {
    ph: 'Rolling upgrade',
    phc: 'hmq-ph-up',
    title: `node-1 rejoins the cluster on ${NEW}`,
    n: [
      { s: 'serving', v: 'new', c: 7500 },
      { s: 'serving', v: 'old', c: 7500 },
      { s: 'serving', v: 'old', c: 7500 },
      { s: 'serving', v: 'new', c: 7500 },
    ],
    m: { nodes: '4', clients: '≈30,000', state: 'Rebalancing', tput: '≈451k/s' },
    ev: [
      ['cluster', `node-1 restarts on ${NEW}, rejoins the cluster and re-syncs its partitions.`],
      [
        'clients',
        'The load balancer adds node-1 back to the pool, traffic spreads four ways again.',
      ],
      ['safety', `node-1 upgraded ✓, 1 of 3 original nodes now running HiveMQ ${NEW}.`],
    ],
  },
  {
    ph: 'Rolling upgrade',
    phc: 'hmq-ph-up',
    title: `Upgrade node-2: drain, leave cluster, install ${NEW}`,
    n: [
      { s: 'serving', v: 'new', c: 10000 },
      { s: 'upg', v: 'new', c: 0 },
      { s: 'serving', v: 'old', c: 10000 },
      { s: 'serving', v: 'new', c: 10000 },
    ],
    m: { nodes: '3', clients: '≈30,000', state: 'Rebalancing', tput: '≈449k/s' },
    ev: [
      [
        'clients',
        'node-2 is drained from the LB rotation; its clients reconnect and round-robin onto node-1/3/4.',
      ],
      [
        'cluster',
        'node-2 leaves the cluster gracefully, its replica partitions migrate to the remaining nodes.',
      ],
      [
        'safety',
        `node-2 is stopped and ${NEW} installed. The already-upgraded node-1 helps absorb the load.`,
      ],
      ['observ', 'Throughput holds steady at ≈449k msg/s · 0 messages lost.'],
    ],
  },
  {
    ph: 'Rolling upgrade',
    phc: 'hmq-ph-up',
    title: `node-2 rejoins the cluster on ${NEW}`,
    n: [
      { s: 'serving', v: 'new', c: 7500 },
      { s: 'serving', v: 'new', c: 7500 },
      { s: 'serving', v: 'old', c: 7500 },
      { s: 'serving', v: 'new', c: 7500 },
    ],
    m: { nodes: '4', clients: '≈30,000', state: 'Rebalancing', tput: '≈452k/s' },
    ev: [
      ['cluster', `node-2 restarts on ${NEW}, rejoins the cluster and re-syncs.`],
      ['clients', 'The load balancer re-adds node-2, connections spread four ways.'],
      ['safety', `node-2 upgraded ✓, 2 of 3 original nodes now on HiveMQ ${NEW}.`],
    ],
  },
  {
    ph: 'Rolling upgrade',
    phc: 'hmq-ph-up',
    title: 'Upgrade node-3, the last original node',
    n: [
      { s: 'serving', v: 'new', c: 10000 },
      { s: 'serving', v: 'new', c: 10000 },
      { s: 'upg', v: 'new', c: 0 },
      { s: 'serving', v: 'new', c: 10000 },
    ],
    m: { nodes: '3', clients: '≈30,000', state: 'Rebalancing', tput: '≈450k/s' },
    ev: [
      ['clients', 'node-3 is drained; its clients reconnect and round-robin onto node-1/2/4.'],
      ['cluster', 'node-3 leaves the cluster gracefully, its replicas migrate to node-1/2/4.'],
      ['safety', `node-3 is stopped and ${NEW} installed, the final original node to be upgraded.`],
      [
        'observ',
        '0 messages lost, persistent sessions and retained messages stayed intact throughout.',
      ],
    ],
  },
  {
    ph: 'Rolling upgrade',
    phc: 'hmq-ph-up',
    title: 'node-3 rejoins, all original nodes upgraded',
    n: [
      { s: 'serving', v: 'new', c: 7500 },
      { s: 'serving', v: 'new', c: 7500 },
      { s: 'serving', v: 'new', c: 7500 },
      { s: 'serving', v: 'new', c: 7500 },
    ],
    m: { nodes: '4', clients: '≈30,000', state: 'Rebalancing', tput: '≈452k/s' },
    ev: [
      ['cluster', `node-3 restarts on ${NEW}, rejoins the cluster and re-syncs.`],
      [
        'safety',
        `node-3 upgraded ✓, all three original nodes now on ${NEW}. The cluster is single-version again.`,
      ],
      ['clients', `All four nodes serve ≈7,500 clients each on HiveMQ ${NEW}.`],
    ],
  },
  {
    ph: 'Scale in',
    phc: 'hmq-ph-scale',
    title: 'Decommission node-4, drain and leave the cluster',
    n: [
      { s: 'serving', v: 'new', c: 10000 },
      { s: 'serving', v: 'new', c: 10000 },
      { s: 'serving', v: 'new', c: 10000 },
      { s: 'drain', v: 'new', c: 0 },
    ],
    m: { nodes: '4', clients: '≈30,000', state: 'Rebalancing', tput: '≈450k/s' },
    ev: [
      [
        'clients',
        'node-4 is removed from the LB rotation; its clients reconnect and round-robin onto node-1/2/3.',
      ],
      [
        'cluster',
        'node-4 leaves the cluster gracefully, its replica partitions migrate back to node-1/2/3.',
      ],
      [
        'safety',
        'The cluster is confirmed at full replication factor 2 on three nodes before node-4 is shut down.',
      ],
      ['observ', 'node-1/2/3 return to ≈10,000 clients each · 0 messages lost.'],
    ],
  },
  {
    ph: 'Scale in',
    phc: 'hmq-ph-scale',
    title: `Upgrade complete, three nodes on ${NEW}`,
    n: [
      { s: 'serving', v: 'new', c: 10000 },
      { s: 'serving', v: 'new', c: 10000 },
      { s: 'serving', v: 'new', c: 10000 },
      { s: 'absent', v: null, c: 0, l: 'Decommissioned' },
    ],
    m: { nodes: '3', clients: '≈30,000', state: 'Healthy', tput: '≈453k/s' },
    ev: [
      [
        'cluster',
        'node-4 is shut down, back to a three-node cluster, replication factor 2, all partitions in sync.',
      ],
      ['safety', `Rolling upgrade complete, every node runs HiveMQ ${NEW} with zero downtime.`],
      ['clients', '≈30,000 clients, ≈10,000 per node · 0 messages lost end-to-end.'],
      ['observ', 'Throughput ≈453k msg/s, no disruption recorded across the entire upgrade.'],
    ],
  },
]

const EV_LABEL: Record<EventTag, { cls: string; label: string }> = {
  cluster: { cls: 'hmq-ev-cluster', label: 'Cluster' },
  clients: { cls: 'hmq-ev-clients', label: 'Clients' },
  safety: { cls: 'hmq-ev-safety', label: 'Safety' },
  observ: { cls: 'hmq-ev-observ', label: 'Metrics' },
}

const NODE_X = [40, 196, 352, 508]
const NODE_CX = [106, 262, 418, 574]

const NODE_CLASS: Record<NodeStatus, string> = {
  serving: 'hmq-n-serving',
  drain: 'hmq-n-drain',
  upg: 'hmq-n-upg',
  join: 'hmq-n-join',
  absent: 'hmq-n-absent',
}

function connText(nd: NodeState): string {
  if (nd.s === 'serving' || nd.s === 'drain') return `${nd.c.toLocaleString()} clients`
  if (nd.s === 'upg') return 'stopped'
  if (nd.s === 'join') return 'warming up'
  return ','
}

function statusText(nd: NodeState): string {
  if (nd.s === 'serving') return 'In rotation'
  if (nd.s === 'drain') return 'Draining'
  if (nd.s === 'upg') return 'Upgrading'
  if (nd.s === 'join') return 'Joining cluster'
  return nd.l ?? ''
}

function stateClass(st: Metrics['state']): string {
  if (st === 'Healthy') return 'hmq-st-healthy'
  if (st === 'Mixed version') return 'hmq-st-mixed'
  return 'hmq-st-rebal'
}

function PlayIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M8 5v14l11-7z" fill="currentColor" />
    </svg>
  )
}

function PauseIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M6 5h4v14H6zm8 0h4v14h-4z" fill="currentColor" />
    </svg>
  )
}

function PrevIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M15 5l-7 7 7 7"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function NextIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M9 5l7 7-7 7"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function RestartIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M19.9 13a8 8 0 1 1-2.2-6.4"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M20 3v5h-5"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

interface NodeProps {
  index: number
  nd: NodeState
}

function ClusterNode({ index, nd }: NodeProps) {
  const x = NODE_X[index]
  const cx = NODE_CX[index]
  const chipClass =
    nd.s === 'absent'
      ? 'hmq-chip hmq-chip-hidden'
      : `hmq-chip ${nd.v === 'new' ? 'hmq-chip-new' : 'hmq-chip-old'}`
  const versionLabel = nd.v === 'new' ? NEW : OLD

  return (
    <g className={`hmq-node ${NODE_CLASS[nd.s]}`} data-n={index + 1}>
      <rect className="hmq-nbx" x={x} y={186} width={132} height={130} rx={10} />
      <text
        className="hmq-svg-t"
        x={cx}
        y={212}
        textAnchor="middle"
        dominantBaseline="central"
        style={{ fontSize: 14, fontWeight: 500 }}
      >
        node-{index + 1}
      </text>
      <g className={chipClass}>
        <rect className="hmq-cbx" x={cx - 42} y={231} width={84} height={22} rx={5} />
        <text
          className="hmq-ctx"
          x={cx}
          y={242}
          textAnchor="middle"
          dominantBaseline="central"
          style={{ fontSize: 12, fontWeight: 500 }}
        >
          {versionLabel}
        </text>
      </g>
      <text
        className="hmq-svg-s hmq-nc"
        x={cx}
        y={270}
        textAnchor="middle"
        dominantBaseline="central"
        style={{ fontSize: 12 }}
      >
        {connText(nd)}
      </text>
      <text
        className="hmq-svg-t hmq-nst"
        x={cx}
        y={296}
        textAnchor="middle"
        dominantBaseline="central"
        style={{ fontSize: 13, fontWeight: 500 }}
      >
        {statusText(nd)}
      </text>
    </g>
  )
}

interface SimulationProps {
  autoplay?: boolean
  interval?: number
  showHeader?: boolean
}

export function HivemqClusterSimulation({
  autoplay = false,
  interval = 3600,
  showHeader = false,
}: SimulationProps) {
  const [idx, setIdx] = useState(0)
  const [playing, setPlaying] = useState(false)
  const { colorMode } = useColorMode()
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const uid = useMemo(() => `hmq-${Math.random().toString(36).slice(2, 8)}`, [])

  const step = STEPS[idx]
  const themeClass = colorMode === 'dark' ? 'hmq-theme-dark' : 'hmq-theme-light'

  useEffect(() => {
    if (autoplay) {
      setPlaying(true)
    }
  }, [autoplay])

  useEffect(() => {
    if (!playing) {
      if (timerRef.current) {
        clearInterval(timerRef.current)
        timerRef.current = null
      }
      return
    }
    timerRef.current = setInterval(() => {
      setIdx((current) => {
        if (current >= STEPS.length - 1) {
          setPlaying(false)
          return current
        }
        return current + 1
      })
    }, interval)
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current)
        timerRef.current = null
      }
    }
  }, [playing, interval])

  const stop = () => setPlaying(false)
  const togglePlay = () => {
    if (playing) {
      setPlaying(false)
      return
    }
    if (idx >= STEPS.length - 1) {
      setIdx(0)
    }
    setPlaying(true)
  }
  const goTo = (n: number) => {
    stop()
    setIdx(Math.max(0, Math.min(STEPS.length - 1, n)))
  }
  const next = () => goTo(idx + 1)
  const prev = () => goTo(idx - 1)
  const restart = () => goTo(0)

  return (
    <div className={`hmq-root ${themeClass}`}>
      {showHeader && (
        <>
          <p className="hmq-widget-title">HiveMQ cluster, rolling upgrade simulation</p>
          <p className="hmq-widget-sub">
            Zero-downtime broker version rotation behind a round-robin load balancer
          </p>
        </>
      )}

      <h2 className="hmq-sr">
        Interactive simulation of a HiveMQ MQTT broker cluster performing a zero-downtime rolling
        upgrade.
      </h2>

      <div className="hmq-headrow">
        <div>
          <span className={`hmq-phase ${step.phc}`}>{step.ph}</span>
          <p className="hmq-header-title">{step.title}</p>
        </div>
        <div className="hmq-counter" aria-live="polite">
          Step {idx + 1} / {STEPS.length}
        </div>
      </div>

      <svg
        className="hmq-svg"
        viewBox="0 0 680 356"
        role="img"
        aria-label="HiveMQ cluster topology: MQTT clients connect through a round-robin load balancer to broker nodes."
      >
        <defs>
          <marker
            id={`${uid}-arrow`}
            viewBox="0 0 10 10"
            refX={8}
            refY={5}
            markerWidth={6}
            markerHeight={6}
            orient="auto-start-reverse"
          >
            <path
              d="M2 1L8 5L2 9"
              fill="none"
              stroke="context-stroke"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </marker>
        </defs>
        <rect
          className="hmq-infra"
          x={240}
          y={16}
          width={200}
          height={44}
          rx={9}
          strokeWidth={0.5}
        />
        <text
          className="hmq-svg-t"
          x={340}
          y={34}
          textAnchor="middle"
          dominantBaseline="central"
          style={{ fontSize: 14, fontWeight: 500 }}
        >
          MQTT clients
        </text>
        <text
          className="hmq-svg-s"
          x={340}
          y={50}
          textAnchor="middle"
          dominantBaseline="central"
          style={{ fontSize: 12 }}
        >
          ≈30,000 sessions · QoS 0/1/2
        </text>
        <line
          className="hmq-conn-feed hmq-flow"
          x1={340}
          y1={62}
          x2={340}
          y2={88}
          markerEnd={`url(#${uid}-arrow)`}
        />
        <rect
          className="hmq-infra"
          x={240}
          y={92}
          width={200}
          height={44}
          rx={9}
          strokeWidth={0.5}
        />
        <text
          className="hmq-svg-t"
          x={340}
          y={110}
          textAnchor="middle"
          dominantBaseline="central"
          style={{ fontSize: 14, fontWeight: 500 }}
        >
          Load balancer
        </text>
        <text
          className="hmq-svg-s"
          x={340}
          y={126}
          textAnchor="middle"
          dominantBaseline="central"
          style={{ fontSize: 12 }}
        >
          round-robin distribution
        </text>
        <rect
          className="hmq-cluster-box"
          x={28}
          y={150}
          width={624}
          height={186}
          rx={14}
          strokeWidth={0.5}
        />
        <text className="hmq-svg-s" x={44} y={170} style={{ fontSize: 12, fontWeight: 500 }}>
          HiveMQ cluster · replication factor 2
        </text>
        {step.n.map((nd, i) => {
          let cls = 'hmq-conn hmq-conn-on hmq-flow'
          if (nd.s === 'absent') cls = 'hmq-conn hmq-conn-hidden'
          else if (nd.s !== 'serving') cls = 'hmq-conn hmq-conn-off'
          return (
            <line
              key={`conn-${i}`}
              className={cls}
              data-n={i + 1}
              x1={340}
              y1={138}
              x2={NODE_CX[i]}
              y2={182}
              markerEnd={`url(#${uid}-arrow)`}
            />
          )
        })}
        {step.n.map((nd, i) => (
          <ClusterNode key={`node-${i}`} index={i} nd={nd} />
        ))}
      </svg>

      <div className="hmq-legend">
        <span className="hmq-leg">
          <span
            className="hmq-sw"
            style={{ background: 'var(--hmq-success-bg)', borderColor: 'var(--hmq-success-fg)' }}
          />
          In rotation
        </span>
        <span className="hmq-leg">
          <span
            className="hmq-sw"
            style={{ background: 'var(--hmq-warning-bg)', borderColor: 'var(--hmq-warning-fg)' }}
          />
          Draining
        </span>
        <span className="hmq-leg">
          <span
            className="hmq-sw"
            style={{ background: 'var(--hmq-surface-2)', borderColor: 'var(--hmq-border-2)' }}
          />
          Stopped / upgrading
        </span>
        <span className="hmq-leg">
          <span
            className="hmq-sw"
            style={{ background: 'var(--hmq-info-bg)', borderColor: 'var(--hmq-info-fg)' }}
          />
          Joining cluster
        </span>
        <span className="hmq-leg" style={{ color: 'var(--hmq-text-3)' }}>
          {OLD} old · {NEW} new
        </span>
      </div>

      <div className="hmq-controls">
        <button type="button" className="hmq-btn hmq-btn-restart" onClick={restart}>
          <RestartIcon />
          Restart
        </button>
        <button type="button" className="hmq-btn hmq-btn-prev" onClick={prev} disabled={idx === 0}>
          <PrevIcon />
          Back
        </button>
        <button type="button" className="hmq-btn hmq-btn-play" onClick={togglePlay}>
          {playing ? <PauseIcon /> : <PlayIcon />}
          <span className="hmq-play-lbl">{playing ? 'Pause' : 'Play'}</span>
        </button>
        <button
          type="button"
          className="hmq-btn hmq-btn-next"
          onClick={next}
          disabled={idx === STEPS.length - 1}
        >
          Next
          <NextIcon />
        </button>
        <div className="hmq-dots" role="tablist" aria-label="Simulation steps">
          {STEPS.map((_, k) => {
            const cls = 'hmq-dot' + (k === idx ? ' hmq-dot-on' : k < idx ? ' hmq-dot-done' : '')
            return (
              <button
                key={`dot-${k}`}
                type="button"
                className={cls}
                aria-label={`Go to step ${k + 1}`}
                onClick={() => goTo(k)}
              />
            )
          })}
        </div>
      </div>

      <div className="hmq-metrics">
        <div className="hmq-mcard">
          <p className="hmq-mlabel">Cluster nodes</p>
          <p className="hmq-mval">{step.m.nodes}</p>
        </div>
        <div className="hmq-mcard">
          <p className="hmq-mlabel">Clients connected</p>
          <p className="hmq-mval">{step.m.clients}</p>
        </div>
        <div className="hmq-mcard">
          <p className="hmq-mlabel">Cluster state</p>
          <p className="hmq-mval" style={{ fontSize: 14 }}>
            <span className={`hmq-pill ${stateClass(step.m.state)}`}>{step.m.state}</span>
          </p>
        </div>
        <div className="hmq-mcard">
          <p className="hmq-mlabel">Throughput</p>
          <p className="hmq-mval">{step.m.tput}</p>
        </div>
        <div className="hmq-mcard">
          <p className="hmq-mlabel">Messages lost</p>
          <p className="hmq-mval" style={{ color: 'var(--hmq-success-fg)' }}>
            0
          </p>
        </div>
      </div>

      <div className="hmq-card">
        <p className="hmq-log-label">What happens this step</p>
        <div className="hmq-log" aria-live="polite">
          {step.ev.map(([tag, text], e) => {
            const meta = EV_LABEL[tag]
            return (
              <div key={`ev-${e}`} className="hmq-evrow">
                <span className={`hmq-evtag ${meta.cls}`}>{meta.label}</span>
                <span className="hmq-evtext">{text}</span>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default HivemqClusterSimulation
